import React, { useState, useEffect } from 'react';
import {
  Modal,
  TextField,
  Button,
  Typography,
  IconButton,
  Fade,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import CloseIcon from '@material-ui/icons/Close';
import StarIcon from '@material-ui/icons/Star';
import StarBorderIcon from '@material-ui/icons/StarBorder';
import ListIcon from '@material-ui/icons/List';
import { useStyles } from './ManageAssetsModalStyles';

interface Asset {
  name: string;
  isDefault: boolean;
}

interface ManageAssetsModalProps {
  open: boolean;
  onClose: () => void;
  assets: Asset[];
  onSave: (assets: Asset[]) => void;
}

export default function ManageAssetsModal({
  open,
  onClose,
  assets,
  onSave,
}: ManageAssetsModalProps) {
  const classes = useStyles();
  const [localAssets, setLocalAssets] = useState<Asset[]>([]);
  const [isInitialized, setIsInitialized] = useState(false);
  const [errorMessage, setErrorMessage] = useState('');

  useEffect(() => {
    if (open && !isInitialized) {
      const initializedAssets = Array.isArray(assets) && assets.length > 0
        ? assets.map(a => ({
            name: String(a.name || ''),
            isDefault: Boolean(a.isDefault),
          }))
        : [];
      setLocalAssets(initializedAssets);
      setIsInitialized(true);
      setErrorMessage('');
    } else if (!open) {
      setIsInitialized(false);
      setErrorMessage('');
    }
  }, [open, assets, isInitialized]);

  const handleNameChange = (index: number, value: string) => {
    const updated = [...localAssets];
    updated[index] = { ...updated[index], name: value };
    setLocalAssets(updated);
    if (errorMessage) setErrorMessage('');
  };

  const handleSetDefault = (index: number) => {
    const updated = localAssets.map((asset, i) => ({
      ...asset,
      isDefault: i === index,
    }));
    setLocalAssets(updated);
  };

  const handleDelete = (index: number) => {
    const updated = localAssets.filter((_, i) => i !== index);
    setLocalAssets(updated);
  };

  const handleAddNew = () => {
    setLocalAssets([...localAssets, {
      name: '',
      isDefault: localAssets.length === 0,
    }]);
  };

  const handleSave = () => {
    const isValid = localAssets.every(a => a.name.trim());

    if (!isValid) {
      setErrorMessage('Preencha o nome de todos os ativos.');
      return;
    }

    const hasDefault = localAssets.some(a => a.isDefault);
    if (!hasDefault && localAssets.length > 0) {
      const updated = [...localAssets];
      updated[0] = { ...updated[0], isDefault: true };
      setLocalAssets(updated);
      onSave(updated);
    } else {
      onSave(localAssets);
    }
    onClose();
  };

  const handleCancel = () => {
    setLocalAssets([...assets]);
    onClose();
  };

  return (
    <Modal
      open={open}
      onClose={handleCancel}
      className={classes.modal}
    >
      <Fade in={open}>
        <div className={classes.paper}>
          <div className={classes.header}>
            <div>
              <Typography className={classes.title}>
                Gerenciar Ativos
              </Typography>
              <Typography className={classes.subtitle}>
                {localAssets.length} ativo{localAssets.length !== 1 ? 's' : ''} cadastrado{localAssets.length !== 1 ? 's' : ''}
              </Typography>
            </div>
            <IconButton className={classes.closeButton} onClick={handleCancel} size="small">
              <CloseIcon fontSize="small" />
            </IconButton>
          </div>

          <div className={classes.itemList}>
            {localAssets.length === 0 ? (
              <div className={classes.emptyState}>
                <ListIcon className={classes.emptyIcon} />
                <Typography className={classes.emptyText}>
                  Nenhum ativo cadastrado.<br />
                  Adicione um novo ativo para comecar.
                </Typography>
              </div>
            ) : (
              localAssets.map((asset, index) => (
                <div key={index} className={classes.assetRow}>
                  <span className={classes.rowIndex}>{index + 1}</span>
                  <TextField
                    className={classes.input}
                    label="Nome"
                    value={asset.name}
                    onChange={(e) => handleNameChange(index, e.target.value)}
                    variant="outlined"
                    size="small"
                    placeholder="Ex: EUR/USD"
                  />
                  <IconButton
                    className={asset.isDefault ? classes.defaultButtonActive : classes.defaultButton}
                    onClick={() => handleSetDefault(index)}
                    size="small"
                    title={asset.isDefault ? 'Ativo padrao' : 'Definir como padrao'}
                  >
                    {asset.isDefault ? <StarIcon fontSize="small" /> : <StarBorderIcon fontSize="small" />}
                  </IconButton>
                  <IconButton
                    className={classes.deleteButton}
                    onClick={() => handleDelete(index)}
                    size="small"
                    title="Remover ativo"
                  >
                    <DeleteIcon fontSize="small" />
                  </IconButton>
                </div>
              ))
            )}
          </div>

          {errorMessage && (
            <Typography className={classes.errorMessage}>
              {errorMessage}
            </Typography>
          )}

          <Button
            className={classes.addButton}
            variant="outlined"
            startIcon={<AddIcon />}
            onClick={handleAddNew}
          >
            Adicionar novo ativo
          </Button>

          <div className={classes.actionButtons}>
            <Button
              onClick={handleCancel}
              variant="outlined"
              className={classes.cancelButton}
            >
              Cancelar
            </Button>
            <Button
              onClick={handleSave}
              variant="contained"
              className={classes.saveButton}
              disabled={localAssets.length === 0}
            >
              Salvar
            </Button>
          </div>
        </div>
      </Fade>
    </Modal>
  );
}
